import { useRef } from 'react';
import {
  DateFieldState,
  useDateFieldState,
  DateSegment as DateSegmentProps,
  useTimeFieldState,
  TimeFieldStateOptions,
  ValidationState,
} from 'react-stately';
import {
  AriaDatePickerProps,
  useDateField,
  useDateSegment,
  useLocale,
  useTimeField,
} from 'react-aria';
import { createCalendar, DateValue } from '@internationalized/date';

export function DateField(props: AriaDatePickerProps<DateValue>) {
  const { locale } = useLocale();
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef(null);
  const { fieldProps } = useDateField(props, state, ref);

  return (
    <div {...fieldProps} ref={ref} className="flex flex-wrap max-w-full">
      {state.segments.map((segment, i) => (
        <DateSegment key={i} segment={segment} state={state} />
      ))}
    </div>
  );
}

function DateSegment({
  segment,
  state,
}: {
  segment: DateSegmentProps;
  state: DateFieldState;
}) {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);
  return (
    <div
      {...segmentProps}
      ref={ref}
      style={{
        ...segmentProps.style,
        minWidth:
          segment.maxValue != null
            ? String(segment.maxValue).length + 'ch'
            : undefined,
      }}
      className={`px-0.5  box-content tabular-nums text-right  outline-none rounded-sm focus:bg-primary-600 focus:text-white group ${
        !segment.isEditable ? 'text-gray-500' : 'text-gray-800'
      }`}
    >
      {/* Always reserve space for the placeholder, to prevent layout shift when editing. */}
      <span
        aria-hidden="true"
        className={`block w-full text-[0.65rem]  ${
          segment.isPlaceholder ? 'mt-1' : 'h-0 invisible'
        } text-center italic text-gray-500 group-focus:text-white pointer-events-none`}
      >
        {segment.placeholder}
      </span>
      {segment.isPlaceholder ? '' : segment.text}
    </div>
  );
}

interface TimeFieldProps extends TimeFieldStateOptions {
  className?:
    | ((validationState: ValidationState | undefined) => string)
    | string;
}
export function TimeField({
  className = 'mt-2 rounded-md border border-primary-500 border-opacity-30 flex justify-center items-center p-2',
  ...props
}: TimeFieldProps) {
  const { locale } = useLocale();
  const state = useTimeFieldState({
    ...props,
    locale,
  });
  const ref = useRef(null);
  const { labelProps, fieldProps } = useTimeField(props, state, ref);
  return (
    <div
      className={
        typeof className === 'function'
          ? className(state.validationState)
          : className
      }
    >
      <label {...labelProps}>{props.label}</label>
      <div {...fieldProps} ref={ref} className="ml-2 inline-flex">
        {state.segments.map((segment, i) => (
          <DateSegment key={i} segment={segment} state={state} />
        ))}
      </div>
    </div>
  );
}
